import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Card,
  CardBody,
  CardTitle,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import api from "@/api/api.js";
import i18n from "@/i18n.js";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePackages } from "@/api/packages.js";
import PackagesShowcase from "@/pages/shared/packages/PackagesShowcase.jsx";
import Swal from "sweetalert2";
import { blockingPurchase, isSpeiReference } from "@/utils/onlinePurchase.js";
import { formatDate } from "@/utils/formatDate.js";

const BuyPackage = () => {
  const { t } = useTranslation();
  const { packages, loading, error } = usePackages();
  const [canRequest, setCanRequest] = useState(false);
  const userId = sessionStorage.getItem("user");
  const [packageRequest, setPackageRequest] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Qué botón abrió el pago, para mostrarlo ocupado mientras se va a Conekta.
  const [opening, setOpening] = useState(null);
  // Si a este usuario se le ofrece pagar en línea. Mientras no se sepa, o si
  // el servidor dice que no, queda la compra de siempre: SPEI con comprobante.
  const [online, setOnline] = useState(false);

  useEffect(() => {
    api
      .get("/payments/checkout")
      .then(({ data }) => setOnline(data.available === true))
      .catch(() => setOnline(false));
  }, []);
  const navigate = useNavigate();

  // No se puede comprar paquetes sin una compañía: redirige a crear una.
  useEffect(() => {
    const userId = sessionStorage.getItem("user");
    if (!userId) return;
    api
      .get(`/users/${userId}.json`)
      .then(({ data }) => {
        if (!data.companies || data.companies.length === 0) {
          Swal.fire({ icon: "info", text: i18n.t("ui.accountFirstHint") });
          navigate("/onboarding/company");
        }
      })
      .catch(() => {});
  }, [navigate]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    multiple: false,
    disabled: !canRequest,
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
    },
  });

  const handleUpload = async () => {
    if (!files.length || !selectedPackage) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", files[0]);
    formData.append("packageId", selectedPackage.id);

    try {
      await api.post(`/request_tickets`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(`/request_tickets`);
      handleCancel();
    } catch (error) {
      Swal.fire({
        icon: "error",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "No se pudo procesar la solicitud",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Manejar cancelación
  const handleCancel = () => {
    setSelectedPackage(null);
    setFiles([]);
    files.forEach((file) => URL.revokeObjectURL(file.preview));
  };

  const fetchRequestAvailable = async () => {
    try {
      // Pendiente es lo que nadie ha resuelto, no lo que no tiene quien lo
      // aprobó: un pago en línea se aprueba solo y nunca tendrá aprobador.
      const response = await api.get(
        `/request_tickets/${userId}/all?approvalStatus=null`,
      );
      const pending = blockingPurchase(response.data["hydra:member"][0]);
      setCanRequest(!pending);
      setPackageRequest(pending);
    } catch {
      setCanRequest(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchRequestAvailable();
  }, [userId]);

  /**
   * Abre el pago en Conekta y manda al usuario allá. El monto no viaja desde
   * aquí: el servidor lo toma del catálogo.
   */
  const startCheckout = async (pkg, channel) => {
    setOpening(`${pkg.id}-${channel}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        packageId: pkg.id,
        channel,
      });
      window.location.assign(data.checkoutUrl);
    } catch (error) {
      setOpening(null);
      const data = error?.response?.data;
      if (data?.checkoutUrl) {
        const { isConfirmed } = await Swal.fire({
          icon: "info",
          text: data.error,
          showCancelButton: true,
          confirmButtonText: t("packages.seeSpeiReference"),
          cancelButtonText: t("packages.close"),
        });
        if (isConfirmed) window.location.assign(data.checkoutUrl);
        return;
      }
      Swal.fire({
        icon: "error",
        text: data?.error || t("packages.checkoutError"),
      });
    }
  };

  /**
   * Pie de cada tarjeta. Con el pago en línea, tarjeta y SPEI se pagan en
   * Conekta y acreditan solos; transferir y subir el comprobante sigue para
   * quien ya pagó así. Sin él, lo de siempre: SPEI abre el comprobante y la
   * tarjeta se ve, pero todavía no.
   */
  const renderActions = (pkg) =>
    online ? (
      <>
        {["tarjeta", "spei"].map((channel) => (
          <button
            key={channel}
            type="button"
            className="cp-cta-plan"
            disabled={!canRequest || opening !== null}
            onClick={() => startCheckout(pkg, channel)}
          >
            {opening === `${pkg.id}-${channel}`
              ? t("packages.opening")
              : t(
                  channel === "tarjeta"
                    ? "packages.payCard"
                    : "packages.paySpei",
                )}
          </button>
        ))}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 cp-cta-nota"
          disabled={!canRequest || opening !== null}
          onClick={() => setSelectedPackage(pkg)}
        >
          {t("packages.payTransfer")}
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          className="cp-cta-plan"
          disabled={!canRequest}
          onClick={() => setSelectedPackage(pkg)}
        >
          {t("packages.paySpei")}
        </button>
        <button
          type="button"
          className="cp-cta-plan cp-cta-espera"
          disabled
          title={t("packages.payCardSoon")}
        >
          {t("packages.payCard")}
        </button>
        <p className="cp-cta-nota">{t("packages.payCardSoon")}</p>
      </>
    );

  return (
    <>
      <Col
        xs={12}
        style={{
          display: !canRequest ? "block" : "none",
        }}
      >
        <Card>
          <CardBody>
            {isSpeiReference(packageRequest) ? (
              <Alert variant="warning" className="mt-4">
                <h5>{t("packages.speiPendingTitle")}</h5>
                <p>
                  {t("packages.speiPendingText", {
                    name: packageRequest.packageName,
                    date: formatDate(packageRequest.paymentOrder.expiresAt),
                  })}
                </p>
                <Button
                  variant="primary"
                  href={packageRequest.paymentOrder.checkoutUrl}
                >
                  {t("packages.seeSpeiReference")}
                </Button>
              </Alert>
            ) : (
              <Alert variant="warning" className="mt-4">
                <h5>¡Tienes una solicitud pendiente!</h5>
                <p className="mb-0">
                  Actualmente tienes una solicitud de paquete en revisión.
                  Podrás realizar nuevas compras una vez que sea aprobada.
                </p>
              </Alert>
            )}
            {!canRequest &&
              packageRequest &&
              !isSpeiReference(packageRequest) && (
                <Alert variant="info" className="mt-4">
                  Se ha detectado una solicitud de{" "}
                  <Link to={`/request_tickets/${packageRequest.id}/show`}>
                    paquete {packageRequest.packageName}
                  </Link>{" "}
                  , solicitada por:
                  {packageRequest.requestUser.userProfile.name}{" "}
                  {packageRequest.requestUser.userProfile.lastname}
                </Alert>
              )}
          </CardBody>
        </Card>
      </Col>
      <Col xs={12}>
        <Card className="mb-4">
          <CardBody>
            <CardTitle className="mb-4">{t("packages.select")}</CardTitle>

            {loading && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                {t("packages.loading")}
              </div>
            )}
            {error && <Alert variant="danger">{t("packages.loadError")}</Alert>}

            <PackagesShowcase
              packages={packages}
              renderActions={renderActions}
              selectedId={selectedPackage?.id}
            />

            {selectedPackage && (
              <div className="mt-4">
                <p className="text-muted small">
                  <strong>{selectedPackage.name}</strong> —{" "}
                  {t("packages.speiHelp")}
                </p>
                <div
                  {...getRootProps()}
                  className="dropzone border rounded p-4 text-center"
                  style={{ borderStyle: "dashed", backgroundColor: "#f8f9fa" }}
                >
                  <input {...getInputProps()} />
                  {files.length ? (
                    <div>
                      <img
                        src={files[0].preview}
                        alt="Preview"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                      <p className="mt-2 text-muted small">{files[0].name}</p>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">
                      Arrastra tu comprobante aquí
                      <br />
                      <small>o haz clic para seleccionar</small>
                    </p>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      variant="outline-secondary"
                      onClick={handleCancel}
                      disabled={isUploading}
                    >
                      Cancelar
                    </Button>
                    {files.length > 0 && (
                      <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Subiendo...
                          </>
                        ) : (
                          "Confirmar Pago"
                        )}
                      </Button>
                    )}
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Formatos aceptados: JPG, PNG, PDF (Máx. 5MB)
                  </small>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default BuyPackage;
